import api from './api.js';
import { getInstitutionContext } from './institutions.js';
import { sanitizeReturnSlug } from '../validators.js';
import {
  createPkcePair,
  randomUrlToken,
  buildGoogleAuthorizeUrl,
  saveOAuthFlow,
  loadOAuthFlow,
  clearOAuthFlow,
} from './oauth.js';
import { isKnownAdminPath } from '../adminUrl.js';
import { sanitizePortalReturnPath } from '../../portal/portalUrl.js';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

/**
 * Auth Service
 * Passwordless magic-link sign-in for the B2B portal + console, plus the
 * shared context lookup. Endpoints: api/src/modules/auth (SSO magic links).
 *
 * The portal passes `returnOrigin = window.location.origin` so the server mints
 * a link back to THIS school's subdomain (the allowed-origin regex only accepts
 * a single-label https://<slug>.coffeechats.me).
 */

// POST /api/sso/request-magic-link emails the sign-in link. `returnOrigin` is
// the caller's https origin (portal passes window.location.origin).
// `portalSlug` marks this as an institution-portal request. Its presence (even
// as '') switches the server to the admin-only gate: an address with no ACTIVE
// InstitutionAdmin record for this school is silently sent nothing, rather than
// being mailed a link that leads to "we couldn't match your account to a
// school". The response is identical either way, by design — see
// magicLinkController.requestMagicLink.
export const requestMagicLink = async ({ email, returnOrigin, portalSlug = '' }) => {
  const response = await api.post('/sso/request-magic-link', { email, returnOrigin, portalSlug });
  return response.data;
};

// GET /api/sso/verify-magic-link/:token exchanges the one-click token for a
// cookie session. `rememberMe` extends token lifetime server-side.
export const verifyMagicLink = async (token, rememberMe) => {
  const response = await api.get(`/sso/verify-magic-link/${token}`, {
    params: rememberMe ? { rememberMe: true } : {},
  });
  return response.data;
};

// POST /api/signout clears THIS realm's session cookie only (the API scopes
// the clear by the X-Auth-Realm header), so signing out of the console leaves a
// student session on coffeechats.me untouched.
export const signout = async () => {
  await api.post('/signout');
};

// Shared context lookup (superAdmin + institutionAdmin scopes). Alias to the
// institutions service so callers don't need to know which module owns it.
export const getMeContext = getInstitutionContext;

// Portal sign-in always enters through one fixed URL. The shared auth page
// validates the internal tenant referrer instead of accepting app routing
// state from query parameters.
const portalAuthOrigin = () => {
  return import.meta.env.VITE_PORTAL_AUTH_ORIGIN || 'https://portal.coffeechats.me';
};

export const buildPortalDirectoryUrl = () => {
  return new URL('/', portalAuthOrigin()).toString();
};

// `notice` is the one piece of state that DOES have to cross this hop as a
// query parameter. A school subdomain and the shared auth origin are separate
// origins, so sessionStorage cannot carry a message between them, and the
// referrer only conveys "who sent you", not "why". It is a closed vocabulary of
// our own literals (never a slug, path, or anything visitor-supplied), so it
// grants no routing authority the referrer check doesn't already own.
export const buildPortalAuthUrl = ({ notice } = {}) => {
  const url = new URL('/auth', portalAuthOrigin());
  if (notice) { url.searchParams.set('notice', notice); }
  return url.toString();
};

export const portalAuthHandoffFromReferrer = (referrer = globalThis.document?.referrer || '') => {
  try {
    const source = new URL(referrer);
    const localMatch = source.hostname.match(/^([a-z0-9-]+)\.localhost$/);
    const productionMatch = source.protocol === 'https:'
      ? source.hostname.match(/^([a-z0-9-]+)\.coffeechats\.me$/)
      : null;
    const slug = sanitizeReturnSlug(localMatch?.[1] || productionMatch?.[1] || '');
    if (!slug) { return null; }
    return {
      slug,
      returnPath: sanitizePortalReturnPath(source.pathname),
    };
  } catch {
    return null;
  }
};

export const navigateToPortalAuth = (
  url = buildPortalAuthUrl(),
  { doc = globalThis.document, preservePath = true } = {},
) => {
  if (!doc?.body) {
    globalThis.window?.location.replace(url);
    return;
  }
  const link = doc.createElement('a');
  link.href = url;
  link.referrerPolicy = preservePath ? 'unsafe-url' : 'origin';
  link.hidden = true;
  link.setAttribute('aria-hidden', 'true');
  doc.body.appendChild(link);
  link.click();
  link.remove();
};

export const hasPortalSession = async (slug, accessToken = null) => {
  try {
    const headers = { 'X-Institution-Slug': slug };
    if (accessToken) { headers.Authorization = `Bearer ${accessToken}`; }
    await api.get('/institutions/me/context', {
      headers,
    });
    return true;
  } catch (err) {
    if (err?.response?.status === 401) { return false; }
    throw err;
  }
};

// Ask the server which school (if any) this Google account administers, with
// no X-Institution-Slug hint. Used only by the shared, slug-less sign-in hop
// so it can route the browser without ever showing a directory of every
// partnered school. Null when the account isn't an active admin anywhere.
export const resolvePortalHome = async (accessToken = null) => {
  try {
    const headers = {};
    if (accessToken) { headers.Authorization = `Bearer ${accessToken}`; }
    const response = await api.get('/institutions/me/context', { headers });
    return { slug: sanitizeReturnSlug(response.data?.institutionAdmin?.slug) };
  } catch (err) {
    if (err?.response?.status === 401) { return { slug: null }; }
    throw err;
  }
};

// Refreshing on the shared auth origin produces an access token that can cross
// localhost's origin boundary safely in the URL fragment. In production the
// refreshed parent-domain cookie is sufficient and the fragment helper is a
// no-op.
export const resumePortalSession = async () => {
  try {
    const response = await api.post('/token/refresh');
    return response.data?.accessToken || null;
  } catch (err) {
    if (err?.response?.status === 401) { return null; }
    throw err;
  }
};

/** True when a Google web client id is configured for this build. */
export const googleSigninConfigured = () => {
  return !!GOOGLE_CLIENT_ID;
};

export const getPendingGoogleSigninSlug = () => {
  try {
    return sanitizeReturnSlug(loadOAuthFlow()?.slug || '');
  } catch (err) {
    return null;
  }
};

export const getPendingGoogleSignin = () => {
  try {
    const flow = loadOAuthFlow();
    const slug = sanitizeReturnSlug(flow?.slug || '');
    if (!slug) { return null; }
    return {
      slug,
      returnPath: sanitizePortalReturnPath(flow?.returnPath),
    };
  } catch {
    return null;
  }
};

// redirect_uri for BOTH the authorize request and the code exchange = this hop's
// own origin + /auth (Google requires an exact match). On portal.coffeechats.me
// that resolves to https://portal.coffeechats.me/auth.
const hopRedirectUri = () => {
  return `${window.location.origin}/auth`;
};

/**
 * Begin Google sign-in for a school `slug`: stash a PKCE verifier + a CSRF state
 * nonce + the slug, then redirect the tab to Google. The page navigates away, so
 * nothing after the redirect runs.
 * @param {string} slug
 */
export const beginGoogleSignin = async (slug, returnPath = '/overview') => {
  const redirectUri = hopRedirectUri();
  const state = randomUrlToken();
  const { codeVerifier, codeChallenge } = await createPkcePair();
  saveOAuthFlow({
    state,
    codeVerifier,
    slug,
    redirectUri,
    returnPath: sanitizePortalReturnPath(returnPath),
  });
  window.location.assign(buildGoogleAuthorizeUrl({
    clientId: GOOGLE_CLIENT_ID,
    redirectUri,
    state,
    codeChallenge,
  }));
};

/**
 * Finish the hop after Google returns `?code=&state=`. Verifies the CSRF nonce
 * against the stash, exchanges the code via POST /signin (the server holds the
 * client secret and sets the .coffeechats.me cookie), and returns the
 * destination slug. A subdomain handoff always stashed one; the shared,
 * slug-less entry point stashed none, so `slug` comes back null and the caller
 * resolves the destination via `resolvePortalHome` instead. Throws on state
 * mismatch / a corrupted stashed slug / server error so the caller can render
 * an error state.
 * @param {{code: string, returnedState: string}} p
 * @returns {Promise<{slug: string|null}>}
 */
export const completeGoogleSignin = async ({ code, returnedState }) => {
  const flow = loadOAuthFlow();
  clearOAuthFlow();
  if (!flow || !flow.state || flow.state !== returnedState) {
    throw new Error('oauth_state_mismatch');
  }
  const slug = flow.slug ? sanitizeReturnSlug(flow.slug) : null;
  if (flow.slug && !slug) {
    throw new Error('oauth_bad_slug');
  }
  const response = await api.post('/signin', {
    auth_code: code,
    redirect_uri: flow.redirectUri,
    code_verifier: flow.codeVerifier,
    rememberMe: true,
  });
  return {
    slug,
    accessToken: response.data?.accessToken || null,
    returnPath: sanitizePortalReturnPath(flow.returnPath),
  };
};

// ---------------------------------------------------------------------------
// Console (admin.coffeechats.me) sign-in. Same PKCE + CSRF flow as the portal,
// but there is no school slug: the redirect_uri is the console's own origin and
// the return leg just reloads the console (the .coffeechats.me cookie is what
// makes admin.coffeechats.me recognize the super-admin). The console origin must
// be registered as an authorized JS origin + redirect URI on the OAuth client.
// ---------------------------------------------------------------------------

/**
 * Begin super-admin console sign-in: stash a PKCE verifier + CSRF state nonce,
 * then redirect the tab to Google with the console's own origin as redirect_uri.
 * @param {{returnPath?: string}} [options] where to land after sign-in; defaults
 *   to the current location. Validated on return by sanitizeConsoleReturnPath.
 */
export const beginConsoleSignin = async ({
  returnPath = `${window.location.pathname}${window.location.search}`,
} = {}) => {
  const redirectUri = window.location.origin;
  const state = randomUrlToken();
  const { codeVerifier, codeChallenge } = await createPkcePair();
  saveOAuthFlow({ state, codeVerifier, slug: null, redirectUri, returnPath });
  window.location.assign(buildGoogleAuthorizeUrl({
    clientId: GOOGLE_CLIENT_ID,
    redirectUri,
    state,
    codeChallenge,
  }));
};

export const sanitizeConsoleReturnPath = (value) => {
  try {
    const target = new URL(String(value || '/'), window.location.origin);
    if (target.origin !== window.location.origin || !isKnownAdminPath(target.pathname)) { return '/'; }
    return `${target.pathname}${target.search}`;
  } catch {
    return '/';
  }
};

/**
 * Finish console sign-in after Google returns `?code=&state=`. Verifies the CSRF
 * nonce and exchanges the code via POST /signin (sets the .coffeechats.me
 * cookie). With no slug, the caller just re-checks the super-admin context. Throws
 * on state mismatch / server error.
 * @param {{code: string, returnedState: string}} p
 */
export const completeConsoleSignin = async ({ code, returnedState }) => {
  const flow = loadOAuthFlow();
  clearOAuthFlow();
  if (!flow || !flow.state || flow.state !== returnedState) {
    throw new Error('oauth_state_mismatch');
  }
  await api.post('/signin', {
    auth_code: code,
    redirect_uri: flow.redirectUri,
    code_verifier: flow.codeVerifier,
    rememberMe: true,
  });
  return { returnPath: sanitizeConsoleReturnPath(flow.returnPath) };
};
